import { useState, useCallback, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import type { VideoOverlay, VideoAspectRatio } from './types';
import {
  DEFAULT_TEXT_OVERLAY,
  DEFAULT_SUBHEADLINE_OVERLAY,
  DEFAULT_CTA_OVERLAY,
  DEFAULT_LOGO_OVERLAY,
} from './types';
import VideoEditorCanvas from './components/VideoEditorCanvas';
import VideoEditorToolbar from './components/VideoEditorToolbar';
import VideoEditorProperties from './components/VideoEditorProperties';
import EraseModal from '@/components/feature/EraseModal';
import { exportVideoWithOverlays } from '@/lib/exportVideoWithOverlays';
import { downloadFile } from '@/lib/download';

export interface VideoEditorLocationState {
  videoUrl: string;
  audioUrl?: string | null;
  voiceoverScript?: string | null;
  aspectRatio?: VideoAspectRatio;
  videoId?: string;
}

function generateId() {
  return `ov_${Math.random().toString(36).slice(2, 9)}`;
}

const MAX_HISTORY = 30;

export default function VideoEditorPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const state = location.state as VideoEditorLocationState | null;

  const videoUrl = state?.videoUrl ?? '';
  const audioUrl = state?.audioUrl ?? null;
  const voiceoverScript = state?.voiceoverScript ?? null;
  const aspectRatio: VideoAspectRatio = state?.aspectRatio ?? '9:16';

  const videoRef = useRef<HTMLVideoElement>(null);
  const syncedAudioRef = useRef<HTMLAudioElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [audioDuration, setAudioDuration] = useState(0);
  const [audioStartTime, setAudioStartTime] = useState(0);
  const [audioTrimStart, setAudioTrimStart] = useState(0);
  const [audioTrimEnd, setAudioTrimEnd] = useState(0);

  const [overlays, setOverlays] = useState<VideoOverlay[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const [history, setHistory] = useState<VideoOverlay[][]>([]);
  const [future, setFuture] = useState<VideoOverlay[][]>([]);

  const [isExporting, setIsExporting] = useState(false);
  const [exportProgress, setExportProgress] = useState(0);
  const [exportError, setExportError] = useState<string | null>(null);
  const [exportSuccess, setExportSuccess] = useState(false);

  // Erase tool state
  const [eraseOpen, setEraseOpen] = useState(false);
  const [eraseImageUrl, setEraseImageUrl] = useState<string | null>(null);
  const eraseFileInputRef = useRef<HTMLInputElement>(null);

  const overlaysRef = useRef(overlays);
  useEffect(() => { overlaysRef.current = overlays; }, [overlays]);

  // Video time tracking
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const syncAudio = () => {
      const audio = syncedAudioRef.current;
      if (!audioUrl || !audio) return;

      const clipDuration = Math.max(0, audioTrimEnd - audioTrimStart);
      const clipEndTime = audioStartTime + clipDuration;
      const shouldPlay = !video.paused && video.currentTime >= audioStartTime && video.currentTime <= clipEndTime;

      if (!shouldPlay) {
        audio.pause();
        return;
      }

      const targetTime = audioTrimStart + (video.currentTime - audioStartTime);
      if (Math.abs(audio.currentTime - targetTime) > 0.18) {
        audio.currentTime = Math.max(audioTrimStart, Math.min(targetTime, audioTrimEnd));
      }
      audio.play().catch(() => undefined);
    };

    const handleTimeUpdate = () => {
      setCurrentTime(video.currentTime);
      syncAudio();
    };
    const handleLoadedMetadata = () => setDuration(video.duration);
    const handlePlay = () => {
      setIsPlaying(true);
      syncAudio();
    };
    const handlePause = () => {
      setIsPlaying(false);
      syncedAudioRef.current?.pause();
    };
    const handleSeeked = () => syncAudio();
    const handleEnded = () => syncedAudioRef.current?.pause();

    video.addEventListener('timeupdate', handleTimeUpdate);
    video.addEventListener('loadedmetadata', handleLoadedMetadata);
    video.addEventListener('play', handlePlay);
    video.addEventListener('pause', handlePause);
    video.addEventListener('seeked', handleSeeked);
    video.addEventListener('ended', handleEnded);

    return () => {
      video.removeEventListener('timeupdate', handleTimeUpdate);
      video.removeEventListener('loadedmetadata', handleLoadedMetadata);
      video.removeEventListener('play', handlePlay);
      video.removeEventListener('pause', handlePause);
      video.removeEventListener('seeked', handleSeeked);
      video.removeEventListener('ended', handleEnded);
    };
  }, [audioUrl, audioStartTime, audioTrimStart, audioTrimEnd]);

  useEffect(() => {
    if (!audioUrl) return;
    const audio = new Audio();
    audio.preload = 'metadata';
    audio.src = audioUrl;
    const handleLoadedMetadata = () => {
      const loadedDuration = Number.isFinite(audio.duration) ? audio.duration : 0;
      setAudioDuration(loadedDuration);
      setAudioTrimStart(0);
      setAudioTrimEnd(loadedDuration);
      setAudioStartTime(0);
    };
    audio.addEventListener('loadedmetadata', handleLoadedMetadata);
    return () => {
      audio.removeEventListener('loadedmetadata', handleLoadedMetadata);
    };
  }, [audioUrl]);

  // Redirect if no video
  useEffect(() => {
    if (!videoUrl) {
      navigate('/dashboard/generate', { replace: true });
    }
  }, [videoUrl, navigate]);

  // ── History ────────────────────────────────────────────────────
  const pushHistory = useCallback(() => {
    const snap = overlaysRef.current;
    setHistory((h) => [...h.slice(-MAX_HISTORY), snap]);
    setFuture([]);
  }, []);

  const updateOverlays = useCallback((updater: (prev: VideoOverlay[]) => VideoOverlay[]) => {
    setOverlays((prev) => {
      setHistory((h) => [...h.slice(-MAX_HISTORY), prev]);
      setFuture([]);
      return updater(prev);
    });
  }, []);

  const undo = useCallback(() => {
    setHistory((h) => {
      if (h.length === 0) return h;
      const prev = h[h.length - 1];
      const snap = overlaysRef.current;
      setFuture((f) => [snap, ...f.slice(0, MAX_HISTORY - 1)]);
      setOverlays(prev);
      return h.slice(0, -1);
    });
  }, []);

  const redo = useCallback(() => {
    setFuture((f) => {
      if (f.length === 0) return f;
      const next = f[0];
      const snap = overlaysRef.current;
      setHistory((h) => [...h.slice(-MAX_HISTORY), snap]);
      setOverlays(next);
      return f.slice(1);
    });
  }, []);

  // ── Add overlays ───────────────────────────────────────────────
  const addText = useCallback((preset: 'headline' | 'subheadline') => {
    const base = preset === 'headline' ? DEFAULT_TEXT_OVERLAY : DEFAULT_SUBHEADLINE_OVERLAY;
    const newOverlay: VideoOverlay = { ...base, id: generateId(), preset };
    updateOverlays((prev) => [...prev, newOverlay]);
    setSelectedId(newOverlay.id);
  }, [updateOverlays]);

  const addCta = useCallback(() => {
    const newOverlay: VideoOverlay = { ...DEFAULT_CTA_OVERLAY, id: generateId() };
    updateOverlays((prev) => [...prev, newOverlay]);
    setSelectedId(newOverlay.id);
  }, [updateOverlays]);

  const addLogo = useCallback((file: File) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const dataUrl = e.target?.result as string;
      if (!dataUrl) return;
      const newOverlay: VideoOverlay = { ...DEFAULT_LOGO_OVERLAY, id: generateId(), imageUrl: dataUrl };
      updateOverlays((prev) => [...prev, newOverlay]);
      setSelectedId(newOverlay.id);
    };
    reader.readAsDataURL(file);
  }, [updateOverlays]);

  const deleteOverlay = useCallback((id: string) => {
    updateOverlays((prev) => prev.filter((o) => o.id !== id));
    setSelectedId(null);
  }, [updateOverlays]);

  const duplicateOverlay = useCallback((id: string) => {
    const overlay = overlays.find((o) => o.id === id);
    if (!overlay) return;
    const newOverlay: VideoOverlay = {
      ...overlay,
      id: generateId(),
      x: Math.min(overlay.x + 3, 90),
      y: Math.min(overlay.y + 3, 90),
    };
    updateOverlays((prev) => [...prev, newOverlay]);
    setSelectedId(newOverlay.id);
  }, [overlays, updateOverlays]);

  const updateOverlay = useCallback((id: string, updates: Partial<VideoOverlay>) => {
    setOverlays((prev) => prev.map((o) => (o.id === id ? { ...o, ...updates } : o)));
  }, []);

  const replaceLogoImage = useCallback((id: string, file: File) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const dataUrl = e.target?.result as string;
      if (!dataUrl) return;
      updateOverlay(id, { imageUrl: dataUrl });
    };
    reader.readAsDataURL(file);
  }, [updateOverlay]);

  // ── Playback ───────────────────────────────────────────────────
  const togglePlay = useCallback(() => {
    const video = videoRef.current;
    if (!video) return;
    if (video.paused) {
      video.play();
    } else {
      video.pause();
    }
  }, []);

  const handleSeek = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const video = videoRef.current;
    if (!video) return;
    video.currentTime = Number(e.target.value);
  }, []);

  // ── Export ─────────────────────────────────────────────────────
  const handleExport = useCallback(async () => {
    if (isExporting) return;
    setIsExporting(true);
    setExportProgress(0);
    setExportError(null);
    setExportSuccess(false);

    try {
      await exportVideoWithOverlays({
        videoUrl,
        overlays,
        aspectRatio,
        audio: audioUrl
          ? {
              url: audioUrl,
              startTime: audioStartTime,
              trimStart: audioTrimStart,
              trimEnd: audioTrimEnd,
              volume: 1,
            }
          : null,
        onProgress: setExportProgress,
      });
      setExportSuccess(true);
      setTimeout(() => setExportSuccess(false), 4000);
    } catch (err) {
      setExportError(err instanceof Error ? err.message : 'Export failed. Please try again.');
      setTimeout(() => setExportError(null), 6000);
    } finally {
      setIsExporting(false);
      setExportProgress(0);
    }
  }, [isExporting, videoUrl, overlays, aspectRatio, audioUrl, audioStartTime, audioTrimStart, audioTrimEnd]);

  const handleDownloadOriginal = useCallback(() => {
    downloadFile(videoUrl, `adforge-video-original-${Date.now()}.mp4`);
  }, [videoUrl]);

  const handleDownloadVoiceover = useCallback(() => {
    if (!audioUrl) return;
    downloadFile(audioUrl, `adforge-voiceover-${Date.now()}.mp3`);
  }, [audioUrl]);

  const handleEraseFileChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      const dataUrl = ev.target?.result as string;
      if (dataUrl) { setEraseImageUrl(dataUrl); setEraseOpen(true); }
    };
    reader.readAsDataURL(file);
    e.target.value = '';
  }, []);

  const handleEraseSave = useCallback((newImageUrl: string) => {
    const newOverlay: VideoOverlay = {
      ...DEFAULT_LOGO_OVERLAY,
      id: `ov_${Math.random().toString(36).slice(2, 9)}`,
      imageUrl: newImageUrl,
      x: 10,
      y: 10,
      width: 40,
    };
    updateOverlays((prev) => [...prev, newOverlay]);
    setSelectedId(newOverlay.id);
    setEraseOpen(false);
    setEraseImageUrl(null);
  }, [updateOverlays]);

  // ── Keyboard shortcuts ─────────────────────────────────────────
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      const ctrl = e.ctrlKey || e.metaKey;
      if (!ctrl) return;
      const key = e.key.toLowerCase();
      if (key === 'z' && !e.shiftKey) { e.preventDefault(); undo(); }
      else if (key === 'y' || (key === 'z' && e.shiftKey)) { e.preventDefault(); redo(); }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [undo, redo]);

  const selectedOverlay = overlays.find((o) => o.id === selectedId) ?? null;

  if (!videoUrl) return null;

  const handleEraseButtonClick = () => eraseFileInputRef.current?.click();

  const formatTime = (t: number) => {
    const m = Math.floor(t / 60);
    const s = Math.floor(t % 60);
    const tenths = Math.floor((t % 1) * 10);
    return `${m}:${s.toString().padStart(2, '0')}.${tenths}`;
  };

  const audioClipDuration = Math.max(0, audioTrimEnd - audioTrimStart);
  const audioEndTime = audioStartTime + audioClipDuration;
  const audioExtendsPastVideo = !!audioUrl && duration > 0 && audioEndTime > duration + 0.05;

  const handleAudioStartChange = (value: number) => {
    setAudioStartTime(Math.max(0, Math.min(value, Math.max(duration - 0.1, 0))));
  };

  const handleTrimStartChange = (value: number) => {
    const maxStart = Math.max(0, audioTrimEnd - 0.1);
    setAudioTrimStart(Math.max(0, Math.min(value, maxStart)));
  };

  const handleTrimEndChange = (value: number) => {
    const minEnd = Math.min(audioDuration, audioTrimStart + 0.1);
    setAudioTrimEnd(Math.max(minEnd, Math.min(value, audioDuration)));
  };

  return (
    <div className="fixed inset-0 z-50 bg-stone-950 flex flex-col overflow-hidden">
      {audioUrl && (
        <audio ref={syncedAudioRef} src={audioUrl} preload="auto" className="hidden" />
      )}
      {/* Hidden file input for erase tool */}
      <input ref={eraseFileInputRef} type="file" accept="image/*" className="hidden" onChange={handleEraseFileChange} />

      {/* Erase Modal */}
      {eraseOpen && eraseImageUrl && (
        <EraseModal
          imageUrl={eraseImageUrl}
          onSave={handleEraseSave}
          onClose={() => { setEraseOpen(false); setEraseImageUrl(null); }}
        />
      )}

      {/* ── Top Bar ─────────────────────────────────────────────── */}
      <div className="h-14 bg-stone-900 border-b border-stone-800 flex items-center px-4 gap-3 shrink-0">
        {/* Left: title */}
        <div className="flex items-center gap-2.5 min-w-0">
          <div className="w-7 h-7 flex items-center justify-center rounded-lg bg-gradient-to-br from-rose-500 to-orange-400 shrink-0">
            <i className="ri-film-fill text-white text-sm" />
          </div>
          <div className="min-w-0">
            <span className="text-sm font-bold text-white whitespace-nowrap">Video Editor Mode</span>
            <span className="ml-2 text-xs text-stone-500 hidden sm:inline">Add overlays to your video</span>
          </div>
        </div>

        <div className="flex-1" />

        {/* Center: playback controls */}
        <div className="hidden md:flex items-center gap-2">
          <button
            onClick={undo}
            disabled={history.length === 0}
            className="w-7 h-7 flex items-center justify-center rounded-lg text-stone-500 hover:text-stone-300 hover:bg-stone-800 disabled:opacity-30 disabled:cursor-not-allowed transition-colors cursor-pointer"
            title="Undo (Ctrl+Z)"
          >
            <i className="ri-arrow-go-back-line text-sm" />
          </button>
          <button
            onClick={redo}
            disabled={future.length === 0}
            className="w-7 h-7 flex items-center justify-center rounded-lg text-stone-500 hover:text-stone-300 hover:bg-stone-800 disabled:opacity-30 disabled:cursor-not-allowed transition-colors cursor-pointer"
            title="Redo (Ctrl+Y)"
          >
            <i className="ri-arrow-go-forward-line text-sm" />
          </button>
          <div className="w-px h-5 bg-stone-800 mx-1" />
          <button
            onClick={togglePlay}
            className="w-8 h-8 flex items-center justify-center rounded-lg bg-stone-800 hover:bg-stone-700 text-stone-300 hover:text-white transition-colors cursor-pointer"
          >
            <i className={`${isPlaying ? 'ri-pause-fill' : 'ri-play-fill'} text-base`} />
          </button>
          <div className="flex items-center gap-2 text-xs text-stone-500">
            <span className="font-mono">{formatTime(currentTime)}</span>
            <input
              type="range"
              min={0}
              max={duration || 10}
              step={0.1}
              value={currentTime}
              onChange={handleSeek}
              className="w-32 accent-orange-500 cursor-pointer"
            />
            <span className="font-mono">{formatTime(duration)}</span>
          </div>
        </div>

        <div className="flex-1" />

        {/* Right: actions */}
        <div className="flex items-center gap-2">
          <button
            onClick={handleEraseButtonClick}
            className="hidden sm:flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-stone-800 hover:bg-stone-700 border border-stone-700 hover:border-orange-500/50 text-stone-400 hover:text-white text-xs font-semibold transition-all cursor-pointer whitespace-nowrap"
          >
            <i className="ri-eraser-line text-orange-400" />
            Erase Image
            <span className="px-1.5 py-0.5 rounded-full bg-amber-500/20 text-amber-400 text-[10px] font-bold leading-none">Beta</span>
          </button>

          <button
            onClick={handleDownloadOriginal}
            className="hidden sm:flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-stone-800 hover:bg-stone-700 text-stone-400 hover:text-stone-200 text-xs font-semibold transition-all cursor-pointer whitespace-nowrap"
          >
            <i className="ri-download-2-line" />
            Original
          </button>

          {/* Export button */}
          <button
            onClick={handleExport}
            disabled={isExporting}
            className={`flex items-center gap-1.5 px-4 py-1.5 rounded-lg text-xs font-bold transition-all whitespace-nowrap ${
              isExporting
                ? 'bg-stone-700 text-stone-400 cursor-not-allowed'
                : 'bg-gradient-to-r from-rose-500 to-orange-400 hover:from-rose-600 hover:to-orange-500 text-white cursor-pointer'
            }`}
          >
            {isExporting ? (
              <>
                <i className="ri-loader-4-line animate-spin" />
                Exporting {Math.round(exportProgress * 100)}%
              </>
            ) : (
              <>
                <i className="ri-export-line" />
                {audioUrl ? 'Export with Voiceover' : 'Export Video'}
              </>
            )}
          </button>
        </div>
      </div>

      {/* ── Status bar ──────────────────────────────────────────── */}
      {(exportError || exportSuccess) && (
        <div className={`px-4 py-2 text-xs font-semibold flex items-center gap-2 shrink-0 ${
          exportError ? 'bg-red-900/50 text-red-300' : 'bg-green-900/50 text-green-300'
        }`}>
          <i className={exportError ? 'ri-error-warning-line' : 'ri-checkbox-circle-line'} />
          {exportError || 'Video exported successfully! Check your downloads.'}
        </div>
      )}

      {/* ── Export progress bar ──────────────────────────────────── */}
      {isExporting && (
        <div className="h-1 bg-stone-800 shrink-0">
          <div
            className="h-full bg-gradient-to-r from-rose-500 to-orange-400 transition-all duration-300"
            style={{ width: `${exportProgress * 100}%` }}
          />
        </div>
      )}

      {/* ── Main layout ─────────────────────────────────────────── */}
      {audioUrl && (
        <div className="bg-stone-900 border-b border-stone-800 px-4 py-3 shrink-0">
          <div className="flex flex-col xl:flex-row xl:items-center gap-3">
            <div className="flex items-center gap-3 min-w-0 xl:w-80">
              <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-orange-500/15 text-orange-400 shrink-0">
                <i className="ri-mic-line text-sm" />
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-xs font-bold text-stone-200">Voiceover timing</p>
                {voiceoverScript && (
                  <p className="text-xs text-stone-500 truncate">{voiceoverScript}</p>
                )}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 flex-1">
              <label className="min-w-0">
                <span className="flex items-center justify-between text-[11px] font-bold text-stone-500 uppercase tracking-wider mb-1">
                  Start
                  <span className="font-mono text-stone-400">{formatTime(audioStartTime)}</span>
                </span>
                <input
                  type="range"
                  min={0}
                  max={Math.max(duration, 0)}
                  step={0.1}
                  value={audioStartTime}
                  onChange={event => handleAudioStartChange(Number(event.target.value))}
                  className="w-full accent-orange-500 cursor-pointer"
                />
              </label>

              <label className="min-w-0">
                <span className="flex items-center justify-between text-[11px] font-bold text-stone-500 uppercase tracking-wider mb-1">
                  Trim In
                  <span className="font-mono text-stone-400">{formatTime(audioTrimStart)}</span>
                </span>
                <input
                  type="range"
                  min={0}
                  max={Math.max(audioDuration, 0)}
                  step={0.1}
                  value={audioTrimStart}
                  onChange={event => handleTrimStartChange(Number(event.target.value))}
                  className="w-full accent-orange-500 cursor-pointer"
                />
              </label>

              <label className="min-w-0">
                <span className="flex items-center justify-between text-[11px] font-bold text-stone-500 uppercase tracking-wider mb-1">
                  Trim Out
                  <span className="font-mono text-stone-400">{formatTime(audioTrimEnd)}</span>
                </span>
                <input
                  type="range"
                  min={0}
                  max={Math.max(audioDuration, 0)}
                  step={0.1}
                  value={audioTrimEnd}
                  onChange={event => handleTrimEndChange(Number(event.target.value))}
                  className="w-full accent-orange-500 cursor-pointer"
                />
              </label>
            </div>

            <div className="flex items-center gap-2 xl:justify-end">
              <div className={`hidden sm:flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-xs font-semibold ${
                audioExtendsPastVideo
                  ? 'bg-amber-500/10 text-amber-300 border border-amber-500/20'
                  : 'bg-stone-800 text-stone-400 border border-stone-700'
              }`}>
                <i className={audioExtendsPastVideo ? 'ri-error-warning-line' : 'ri-scissors-cut-line'} />
                <span className="whitespace-nowrap">
                  Ends {formatTime(Math.min(audioEndTime, duration || audioEndTime))}
                  {audioExtendsPastVideo ? ' clipped' : ''}
                </span>
              </div>
              <button
                onClick={handleDownloadVoiceover}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-stone-800 hover:bg-stone-700 text-stone-400 hover:text-stone-200 text-xs font-semibold transition-all cursor-pointer whitespace-nowrap"
              >
                <i className="ri-download-2-line" />
                MP3
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="flex flex-1 overflow-hidden">

        {/* Left panel */}
        <VideoEditorToolbar
          overlays={overlays}
          selectedId={selectedId}
          onSelectOverlay={setSelectedId}
          onAddText={addText}
          onAddCta={addCta}
          onAddLogo={addLogo}
          onDeleteOverlay={deleteOverlay}
          onDuplicateOverlay={duplicateOverlay}
          onBack={() => navigate(-1)}
        />

        {/* Center canvas */}
        <div className="flex-1 bg-stone-900 overflow-hidden flex flex-col">
          <VideoEditorCanvas
            videoUrl={videoUrl}
            aspectRatio={aspectRatio}
            overlays={overlays}
            selectedId={selectedId}
            onSelectOverlay={setSelectedId}
            onUpdateOverlay={updateOverlay}
            onInteractionStart={pushHistory}
            videoRef={videoRef}
            currentTime={currentTime}
          />

          {/* Mobile playback bar */}
          <div className="md:hidden flex items-center gap-2 px-4 py-2 bg-stone-900 border-t border-stone-800 shrink-0">
            <button
              onClick={togglePlay}
              className="w-8 h-8 flex items-center justify-center rounded-lg bg-stone-800 text-stone-300 cursor-pointer"
            >
              <i className={`${isPlaying ? 'ri-pause-fill' : 'ri-play-fill'} text-base`} />
            </button>
            <input
              type="range"
              min={0}
              max={duration || 10}
              step={0.1}
              value={currentTime}
              onChange={handleSeek}
              className="flex-1 accent-orange-500 cursor-pointer"
            />
            <span className="text-xs text-stone-500 font-mono whitespace-nowrap">
              {formatTime(currentTime)} / {formatTime(duration)}
            </span>
          </div>
        </div>

        {/* Right panel */}
        <VideoEditorProperties
          selectedOverlay={selectedOverlay}
          onUpdateOverlay={(updates) => {
            if (selectedId) updateOverlay(selectedId, updates);
          }}
          onDeleteOverlay={() => selectedId && deleteOverlay(selectedId)}
          onDuplicateOverlay={() => selectedId && duplicateOverlay(selectedId)}
          onReplaceLogoImage={(file) => { if (selectedId) replaceLogoImage(selectedId, file); }}
        />
      </div>

      {/* ── Info banner (future features) ───────────────────────── */}
      <div className="h-8 bg-stone-950 border-t border-stone-800 flex items-center px-4 gap-3 shrink-0">
        <i className="ri-information-line text-stone-600 text-xs" />
        <span className="text-xs text-stone-600">
          Drag elements to reposition · Click canvas to deselect · Ctrl+Z to undo
        </span>
        <div className="flex-1" />
        <span className="text-xs text-stone-700">
          {overlays.length} overlay{overlays.length !== 1 ? 's' : ''}
        </span>
      </div>
    </div>
  );
}
